import React, { useState, useEffect } from 'react'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Modal } from '../../ui/Modal'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import type { StepData } from '../../../services/onboardingService'
import {
  syntageService,
  type CreateCredentialData,
  type SyntageCredential,
} from '../../../services/syntageService'
import ContinueLaterModal from '../ContinueLaterModal'

interface CiecConsultationStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  stepData?: any
  stepKey?: string
  rfc?: string
}

interface CiecFormData {
  rfc: string
  ciecPassword: string
}

const validationSchema = Yup.object({
  rfc: Yup.string()
    .transform(value => (value ? value.toUpperCase() : value)) // Transform to uppercase before validation
    .matches(/^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/, 'RFC inválido')
    .required('El RFC es requerido'),
  ciecPassword: Yup.string()
    .matches(
      /^[A-Za-z0-9]{8}$/,
      'La contraseña CIEC debe tener exactamente 8 caracteres y solo contener letras y números'
    )
    .length(8, 'La contraseña CIEC debe tener exactamente 8 caracteres')
    .required('La contraseña CIEC es requerida'),
})

// Clock component with animated hands
const AnimatedClock: React.FC = () => {
  const [hourRotation, setHourRotation] = useState(0)
  const [minuteRotation, setMinuteRotation] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setHourRotation(prev => prev + 6) // Move 6 degrees every 500ms (slower)
      setMinuteRotation(prev => prev + 36) // Move 36 degrees every 500ms (faster)
    }, 500)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="w-8 h-10 mr-3 flex-shrink-0">
      <svg viewBox="0 0 24 24" className="w-full h-full">
        {/* Clock face */}
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="#fef3c7"
          stroke="#d97706"
          strokeWidth="1.5"
        />

        {/* Hour markers */}
        {Array.from({ length: 12 }, (_, i) => {
          const angle = i * 30 * (Math.PI / 180)
          const x1 = 12 + 8 * Math.cos(angle)
          const y1 = 12 + 8 * Math.sin(angle)
          const x2 = 12 + 9 * Math.cos(angle)
          const y2 = 12 + 9 * Math.sin(angle)

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#d97706"
              strokeWidth="1"
            />
          )
        })}

        {/* Hour hand */}
        <line
          x1="12"
          y1="12"
          x2="12"
          y2="7"
          stroke="#92400e"
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            transformOrigin: '12px 12px',
            transform: `rotate(${hourRotation}deg)`,
            transition: 'transform 0.5s linear',
          }}
        />

        {/* Minute hand */}
        <line
          x1="12"
          y1="12"
          x2="12"
          y2="5"
          stroke="#b45309"
          strokeWidth="1.5"
          strokeLinecap="round"
          style={{
            transformOrigin: '12px 12px',
            transform: `rotate(${minuteRotation}deg)`,
            transition: 'transform 0.5s linear',
          }}
        />

        {/* Center dot */}
        <circle cx="12" cy="12" r="1.5" fill="#92400e" />
      </svg>
    </div>
  )
}

const CiecConsultationStep: React.FC<CiecConsultationStepProps> = ({
  onComplete,
  rfc,
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<
    'idle' | 'connecting' | 'validating' | 'error' | 'success'
  >('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [attempts, setAttempts] = useState(0)
  const [currentCredential, setCurrentCredential] =
    useState<SyntageCredential | null>(null)
  const [validationProgress, setValidationProgress] = useState('')
  const maxAttempts = 5

  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const handleSuccessConfirm = async () => {
    setShowSuccessModal(false)
    if (currentCredential && onComplete) {
      await onComplete({
        completedAt: new Date().toISOString(),
        data: {
          rfc: currentCredential.rfc,
          credential: currentCredential,
        },
      })
    }
  }

  // Check for existing valid credential on component mount
  useEffect(() => {
    checkExistingCredential()
  }, [])

  const checkExistingCredential = async () => {
    try {
      const hasValid = await syntageService.hasValidCredential()
      if (hasValid) {
        const response = await syntageService.getMyValidCredential()
        if (response.credential) {
          setCurrentCredential(response.credential)
          setConnectionStatus('success')
          toast.success('Ya tienes una credencial CIEC válida')
        }
      }

      // Also get attempt count
      const history = await syntageService.getMyCredentials()
      setAttempts(history.totalAttempts)
    } catch (error) {
      console.error('Error checking existing credential:', error)
    }
  }

  const initialValues: CiecFormData = {
    rfc: rfc?.toUpperCase() || '',
    ciecPassword: '',
  }

  const handleFormSubmit = async (values: CiecFormData) => {
    if (attempts >= maxAttempts) {
      setErrorMessage(
        'Has excedido el número máximo de intentos. Intenta en 8 horas.'
      )
      return
    }

    // Check if we already have a valid credential with the same RFC
    if (
      currentCredential &&
      currentCredential.isValid &&
      currentCredential.rfc === values.rfc.toUpperCase()
    ) {
      toast.success('Ya tienes una credencial CIEC válida para este RFC')
      setConnectionStatus('success')

      if (onComplete) {
        await onComplete({
          completedAt: new Date().toISOString(),
          data: { ...values, credential: currentCredential },
        })
      }
      return
    }

    setConnectionStatus('connecting')
    setErrorMessage('')
    setValidationProgress('Creando credencial en Syntage...')

    try {
      const credentialData: CreateCredentialData = {
        type: 'ciec',
        rfc: values.rfc.toUpperCase(),
        password: values.ciecPassword,
      }

      // Create credential in Syntage
      const response = await syntageService.createCredential(credentialData)

      if (response.fromCache && response.credential.isValid) {
        // Already have valid credential
        setCurrentCredential(response.credential)
        setConnectionStatus('success')
        toast.success('Credencial válida encontrada')

        if (onComplete) {
          await onComplete({
            completedAt: new Date().toISOString(),
            data: { ...values, credential: response.credential },
          })
        }
        return
      }

      // Start validation process
      setCurrentCredential(response.credential)
      setConnectionStatus('validating')
      setValidationProgress('Validando credenciales con el SAT...')

      toast.loading('Validando credenciales con el SAT...', {
        id: 'validation',
      })

      // Poll for validation result
      const validatedCredential = await syntageService.pollCredentialStatus(
        3000,
        60
      ) // 3 minutes max

      toast.dismiss('validation')

      if (!validatedCredential) {
        setConnectionStatus('error')
        setErrorMessage(
          'Tiempo de espera agotado. La validación está tomando más tiempo de lo esperado.'
        )
        toast.error('Tiempo de validación agotado')
        return
      }

      if (
        validatedCredential.status === 'valid' &&
        validatedCredential.isValid
      ) {
        setConnectionStatus('success')
        setCurrentCredential(validatedCredential)
        toast.success('¡Credenciales validadas correctamente!')

        if (onComplete) {
          setTimeout(async () => {
            await onComplete({
              completedAt: new Date().toISOString(),
              data: { ...values, credential: validatedCredential },
            })
          }, 15000)
        }
      } else {
        // Validation failed
        setAttempts(prev => prev + 1)
        setConnectionStatus('error')

        if (validatedCredential.status === 'invalid') {
          setErrorMessage(
            validatedCredential.errorMessage ||
              'Las credenciales son inválidas. Verifica tu RFC y contraseña CIEC.'
          )
          toast.error(
            validatedCredential.errorMessage || 'Credenciales inválidas'
          )
        } else {
          setErrorMessage(
            validatedCredential.errorMessage ||
              'Error en la validación de credenciales.'
          )
          toast.error('Error en validación')
        }
      }
    } catch (error: any) {
      setAttempts(prev => prev + 1)
      setConnectionStatus('error')
      const message = error.message || 'Error inesperado. Inténtalo de nuevo.'
      setErrorMessage(message)
      toast.error(message)
    }
  }

  const handleContinue = async () => {
    if (connectionStatus === 'success' && currentCredential && onComplete) {
      setShowSuccessModal(true)
    }
  }

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-h3 font-bold text-gray-900 mb-4">
          Consulta segura con el SAT
        </h1>
        <p className="text-gray-600">
          Para continuar con el análisis de tu solicitud, necesitamos
          conectarnos con el SAT para consultar tu información financiera. Esta
          conexión es segura y solo se usará con fines de validación.
        </p>
      </div>

      {/* Info Link */}
      <div className="text-center mb-8">
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="text-[#0EA5E9] hover:text-blue-800 text-sm font-medium"
        >
          ¿Qué es la CIEC y para qué se solicita? →
        </button>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <div className="flex justify-center">
            <Form className="space-y-6 max-w-xl w-full">
              {/* RFC Field */}
              <Input
                label="RFC de la empresa*"
                name="rfc"
                placeholder="ABC654654DF"
                value={values.rfc}
                onChange={e =>
                  setFieldValue('rfc', e.target.value.toUpperCase())
                }
                error={touched.rfc && errors.rfc ? errors.rfc : undefined}
                disabled={true}
                maxLength={13}
              />

              {/* CIEC Password Field */}
              <Input
                label="Clave CIEC*"
                name="ciecPassword"
                type="password"
                placeholder={
                  connectionStatus === 'success'
                    ? '********'
                    : 'Debe contener 8 caracteres (letras y números)'
                }
                value={values.ciecPassword}
                onChange={e => {
                  // Filter out non-alphanumeric characters and limit to 8 characters
                  const filteredValue = e.target.value
                    .replace(/[^A-Za-z0-9]/g, '')
                    .slice(0, 8)
                  setFieldValue('ciecPassword', filteredValue)
                }}
                error={
                  touched.ciecPassword && errors.ciecPassword
                    ? errors.ciecPassword
                    : undefined
                }
                disabled={
                  connectionStatus === 'connecting' ||
                  connectionStatus === 'success' ||
                  attempts >= maxAttempts
                }
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
                tooltip="Tu contraseña debe contener exactamente 8 caracteres, únicamente se aceptan letras y números."
                maxLength={8}
              />

              {/* Connection Status */}
              {connectionStatus === 'connecting' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-blue-800">
                      {validationProgress || 'Creando credencial...'}
                    </span>
                  </div>
                </div>
              )}

              {/* Validation Status */}
              {connectionStatus === 'validating' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AnimatedClock />
                    <div>
                      <p className="text-yellow-800 font-medium">
                        Validando credenciales con el SAT
                      </p>
                      <p className="text-yellow-700 text-sm mt-1">
                        Este proceso puede tardar un par de minutos. Por favor
                        espera...
                      </p>
                      {currentCredential && (
                        <p className="text-yellow-600 text-xs mt-2">
                          RFC: {currentCredential.rfc} • Intento:{' '}
                          {currentCredential.attemptNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Status */}
              {connectionStatus === 'error' && errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-red-800 font-medium">{errorMessage}</p>
                      <p className="text-red-700 text-sm mt-1">
                        {attempts >= maxAttempts
                          ? 'Límite de intentos alcanzado'
                          : `Intentos restantes: ${maxAttempts - attempts}. Si excedes el límite, podrás intentar nuevamente en 2 horas.`}
                      </p>
                      <p className="text-red-700 text-sm mt-1">
                        Recuerda que son 8 caracteres y son solo números y
                        letras.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Status */}
              {connectionStatus === 'success' && currentCredential && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-green-800 font-medium">
                        ¡Credenciales CIEC validadas correctamente!
                      </p>
                      <div className="text-green-700 text-sm mt-2 space-y-1">
                        <p>
                          <strong>RFC:</strong> {currentCredential.rfc}
                        </p>
                        {currentCredential.name && (
                          <p>
                            <strong>Nombre:</strong> {currentCredential.name}
                          </p>
                        )}
                        <p>
                          <strong>Validado:</strong>{' '}
                          {new Date(
                            currentCredential.validatedAt ||
                              currentCredential.createdAt
                          ).toLocaleString('es-MX')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full">
                {connectionStatus === 'success' &&
                currentCredential?.isValid ? (
                  // Show continue button when credential is valid
                  <>
                    <Button
                      type="button"
                      onClick={handleContinue}
                      className="px-12 py-3 min-w-[230px] sm:order-2"
                    >
                      Continuar
                    </Button>
                    <div className="sm:order-1">
                      <ContinueLaterModal />
                    </div>
                  </>
                ) : (
                  // Show validation form buttons
                  <>
                    <Button
                      className="px-12 py-3 min-w-[230px] sm:order-2"
                      type="submit"
                      disabled={
                        connectionStatus === 'connecting' ||
                        connectionStatus === 'validating' ||
                        attempts >= maxAttempts
                      }
                      isLoading={
                        connectionStatus === 'connecting' ||
                        connectionStatus === 'validating'
                      }
                    >
                      {connectionStatus === 'validating'
                        ? 'Validando...'
                        : attempts >= maxAttempts
                          ? 'Límite excedido'
                          : 'Validar credenciales'}
                    </Button>

                    <div className="sm:order-1">
                      <ContinueLaterModal
                        disabled={
                          connectionStatus === 'connecting' ||
                          connectionStatus === 'validating'
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </Form>
          </div>
        )}
      </Formik>

      {/* Modal: ¿Qué es la CIEC? */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/assets/ciec-icon.svg"
              alt="CIEC Icon"
              className="w-15 h-15 object-contain"
            />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            ¿Qué es la CIEC?
          </h3>
          <div className="text-sm text-gray-700 space-y-4 mb-6">
            <p>
              La Clave de Identificación Electrónica Confidencial (CIEC) es un
              mecanismo de autenticación del SAT que permite a personas físicas
              y morales acceder a su información financiera de solo consulta
              (estados de cuenta, historial de obligaciones, facturación, etc.).
            </p>
            <div className="text-left">
              <ul className="list-disc pl-5 space-y-1">
                <li>Solo permite visualización de datos.</li>
                <li>
                  No permite realizar trámites, presentar declaraciones o
                  efectuar pagos.
                </li>
                <li>
                  Ciberseguridad: Almacenamiento de claves en entornos
                  encriptados y aislados.
                </li>
                <li>
                  Agiliza la evaluación crediticia mediante acceso directo a
                  información financiera verificada.
                </li>
                <li>
                  Reduce tiempos: Procesos de minutos vs. semanas con
                  documentación física.
                </li>
              </ul>
            </div>
            <a
              href="https://oautos.com.mx/ciec/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1789CF] hover:text-blue-800 text-sm font-medium inline-block"
            >
              Conocer más
            </a>
          </div>
          <div className="flex justify-center">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showSuccessModal} onClose={handleSuccessConfirm} size="sm">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-[#68C72C] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-4">
            La conexión con el SAT se ha completado con éxito
          </h2>

          <Button
            variant="primary"
            onClick={handleSuccessConfirm}
            className="w-full"
          >
            Continuar
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default CiecConsultationStep
